/**
 * Customer form used by the customer pages and the quick-create dialog in
 * request capture. Warns about likely duplicates while typing (never blocks).
 */
import { useMemo, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  COUNTRIES,
  SUPPORTED_LOCALES,
  type AddressDto,
  type CustomerDuplicate,
  type CustomerResponse,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet } from "../../lib/api.js";
import { Field } from "../../components/ui/Field.js";
import {
  FilterTabs,
  SelectInput,
  useDebounced,
} from "../../components/ui/controls.js";

export interface SiteDraft extends AddressDto {
  id?: string;
  label: string;
  key: string;
}

export interface CustomerDraft {
  type: "private" | "company";
  companyName: string;
  salutation: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  preferredLocale: string;
  notes: string;
  billing: AddressDto;
  sites: SiteDraft[];
}

let siteKey = 0;
const nextSiteKey = () => `site-${(siteKey += 1)}`;

const emptyAddress = (): AddressDto => ({
  line1: "",
  line2: "",
  postalCode: "",
  city: "",
  country: "CH",
});

export function emptyCustomer(): CustomerDraft {
  return {
    type: "private",
    companyName: "",
    salutation: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    preferredLocale: "",
    notes: "",
    billing: emptyAddress(),
    sites: [],
  };
}

export function customerToDraft(customer: CustomerResponse): CustomerDraft {
  return {
    type: customer.type === "company" ? "company" : "private",
    companyName: customer.companyName ?? "",
    salutation: customer.salutation ?? "",
    firstName: customer.firstName,
    lastName: customer.lastName,
    phone: customer.phone ?? "",
    email: customer.email ?? "",
    preferredLocale: customer.preferredLocale ?? "",
    notes: customer.notes ?? "",
    billing: customer.billingAddress
      ? { line2: "", ...customer.billingAddress }
      : emptyAddress(),
    sites: customer.sites.map((site) => ({
      id: site.id,
      key: nextSiteKey(),
      label: site.label ?? "",
      line1: site.line1,
      line2: site.line2 ?? "",
      postalCode: site.postalCode,
      city: site.city,
      country: site.country,
    })),
  };
}

const optional = (value: string) => value.trim() || undefined;
const nullable = (value: string) => value.trim() || null;

function addressPayload(address: AddressDto) {
  return {
    line1: address.line1.trim(),
    ...(address.line2?.trim() ? { line2: address.line2.trim() } : {}),
    postalCode: address.postalCode.trim(),
    city: address.city.trim(),
    country: address.country,
  };
}

export function createPayload(draft: CustomerDraft) {
  return {
    type: draft.type,
    ...(draft.type === "company"
      ? { companyName: draft.companyName.trim() }
      : {}),
    salutation: optional(draft.salutation),
    firstName: optional(draft.firstName),
    lastName: draft.lastName.trim(),
    phone: optional(draft.phone),
    email: optional(draft.email),
    preferredLocale: optional(draft.preferredLocale),
    notes: optional(draft.notes),
    billingAddress: addressPayload(draft.billing),
    sites: draft.sites
      .filter((site) => site.line1.trim())
      .map((site) => ({
        ...addressPayload(site),
        label: nullable(site.label),
      })),
  };
}

export function updatePayload(draft: CustomerDraft, version: number) {
  return {
    version,
    type: draft.type,
    companyName: draft.type === "company" ? draft.companyName.trim() : null,
    salutation: nullable(draft.salutation),
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    phone: nullable(draft.phone),
    email: nullable(draft.email),
    preferredLocale: nullable(draft.preferredLocale),
    notes: nullable(draft.notes),
    billingAddress: addressPayload(draft.billing),
    sites: draft.sites
      .filter((site) => site.line1.trim())
      .map((site) => ({
        ...(site.id ? { id: site.id } : {}),
        ...addressPayload(site),
        label: nullable(site.label),
      })),
  };
}

export function customerDraftValid(draft: CustomerDraft): boolean {
  const addressOk = (a: AddressDto) =>
    Boolean(a.line1.trim() && a.postalCode.trim() && a.city.trim());
  return Boolean(
    draft.lastName.trim() &&
    (draft.type === "private" || draft.companyName.trim()) &&
    (draft.phone.trim() || draft.email.trim()) &&
    addressOk(draft.billing),
  );
}

function AddressFields({
  value,
  onChange,
  idPrefix,
}: {
  value: AddressDto;
  onChange: (value: AddressDto) => void;
  idPrefix: string;
}) {
  const { t, i18n } = useTranslation();
  const countryNames = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.resolvedLanguage ?? "en"], {
      type: "region",
    });
    return COUNTRIES.map((code) => ({
      value: code,
      label: names.of(code) ?? code,
    })).sort((a, b) =>
      a.value === "CH"
        ? -1
        : b.value === "CH"
          ? 1
          : a.label.localeCompare(b.label),
    );
  }, [i18n.resolvedLanguage]);
  const set =
    (key: keyof AddressDto) => (event: ChangeEvent<HTMLInputElement>) =>
      onChange({ ...value, [key]: event.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-6">
      <Field
        label={t("common.address")}
        required
        className="sm:col-span-4"
        htmlFor={`${idPrefix}-line1`}
      >
        <InputText
          id={`${idPrefix}-line1`}
          value={value.line1}
          onChange={set("line1")}
          autoComplete="address-line1"
          placeholder={t("customer.streetPlaceholder")}
        />
      </Field>
      <Field
        label={t("common.address2")}
        className="sm:col-span-2"
        htmlFor={`${idPrefix}-line2`}
      >
        <InputText
          id={`${idPrefix}-line2`}
          value={value.line2 ?? ""}
          onChange={set("line2")}
          autoComplete="address-line2"
        />
      </Field>
      <Field
        label={t("common.postalCode")}
        required
        className="sm:col-span-2"
        htmlFor={`${idPrefix}-zip`}
      >
        <InputText
          id={`${idPrefix}-zip`}
          value={value.postalCode}
          onChange={set("postalCode")}
          inputMode="numeric"
          autoComplete="postal-code"
        />
      </Field>
      <Field
        label={t("common.city")}
        required
        className="sm:col-span-2"
        htmlFor={`${idPrefix}-city`}
      >
        <InputText
          id={`${idPrefix}-city`}
          value={value.city}
          onChange={set("city")}
          autoComplete="address-level2"
        />
      </Field>
      <Field label={t("common.country")} className="sm:col-span-2">
        <SelectInput
          value={value.country}
          onChange={(country) => onChange({ ...value, country })}
          options={countryNames}
          filter
        />
      </Field>
    </div>
  );
}

function DuplicateWarning({
  draft,
  excludeId,
}: {
  draft: CustomerDraft;
  excludeId?: string;
}) {
  const { t } = useTranslation();
  const probe = useDebounced(
    {
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      lastName: draft.lastName.trim(),
      companyName: draft.type === "company" ? draft.companyName.trim() : "",
      postalCode: draft.billing.postalCode.trim(),
    },
    500,
  );
  const enabled = Boolean(
    probe.email ||
    probe.phone.length >= 7 ||
    probe.companyName ||
    (probe.lastName && probe.postalCode),
  );
  const duplicates = useQuery({
    queryKey: ["customers", "duplicates", probe, excludeId],
    queryFn: () =>
      apiGet<{ data: CustomerDuplicate[] }>("/customers/duplicates", {
        query: { ...probe, excludeId },
      }).then((response) => response.data),
    enabled,
  });
  if (!enabled || !duplicates.data?.length) return null;
  return (
    <div
      role="status"
      className="rounded-xl border border-warning-border bg-warning-soft px-4 py-3 text-sm text-warning"
    >
      <p className="font-medium">{t("customer.duplicatesTitle")}</p>
      <ul className="mt-1 space-y-1">
        {duplicates.data.map((duplicate) => (
          <li key={duplicate.id}>
            <Link
              to={`/customers/${duplicate.id}`}
              target="_blank"
              className="underline"
            >
              {duplicate.companyName ||
                `${duplicate.firstName} ${duplicate.lastName}`}{" "}
              ({duplicate.number})
            </Link>{" "}
            ·{" "}
            {duplicate.matchedOn
              .map((m) => t(`customer.match.${m}`))
              .join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CustomerForm({
  value,
  onChange,
  excludeId,
  compact,
}: {
  value: CustomerDraft;
  onChange: (value: CustomerDraft) => void;
  excludeId?: string;
  /** Quick-create: hide sites, language and notes. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const set =
    (key: keyof CustomerDraft) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange({ ...value, [key]: event.target.value });
  const contactMissing = !value.phone.trim() && !value.email.trim();
  return (
    <div className="space-y-5">
      <FilterTabs
        value={value.type}
        onChange={(type) => onChange({ ...value, type })}
        options={[
          { value: "private", label: t("customer.types.private") },
          { value: "company", label: t("customer.types.company") },
        ]}
      />
      <div className="grid gap-3 sm:grid-cols-6">
        {value.type === "company" ? (
          <Field
            label={t("customer.companyName")}
            required
            className="sm:col-span-6"
            htmlFor="customer-company"
          >
            <InputText
              id="customer-company"
              value={value.companyName}
              onChange={set("companyName")}
              autoComplete="organization"
            />
          </Field>
        ) : null}
        <Field
          label={t("customer.salutation")}
          className="sm:col-span-2"
          htmlFor="customer-salutation"
        >
          <InputText
            id="customer-salutation"
            value={value.salutation}
            onChange={set("salutation")}
            autoComplete="honorific-prefix"
          />
        </Field>
        <Field
          label={t("customer.firstName")}
          className="sm:col-span-2"
          htmlFor="customer-first"
        >
          <InputText
            id="customer-first"
            value={value.firstName}
            onChange={set("firstName")}
            autoComplete="given-name"
          />
        </Field>
        <Field
          label={t("customer.lastName")}
          required
          className="sm:col-span-2"
          htmlFor="customer-last"
        >
          <InputText
            id="customer-last"
            value={value.lastName}
            onChange={set("lastName")}
            autoComplete="family-name"
          />
        </Field>
        <Field
          label={t("common.phone")}
          className="sm:col-span-3"
          htmlFor="customer-phone"
          hint={contactMissing ? t("customer.contactRequired") : undefined}
        >
          <InputText
            id="customer-phone"
            type="tel"
            inputMode="tel"
            value={value.phone}
            onChange={set("phone")}
            autoComplete="tel"
          />
        </Field>
        <Field
          label={t("common.email")}
          className="sm:col-span-3"
          htmlFor="customer-email"
        >
          <InputText
            id="customer-email"
            type="email"
            inputMode="email"
            value={value.email}
            onChange={set("email")}
            autoComplete="email"
          />
        </Field>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold">
          {t("customer.billingAddress")}
        </h3>
        <AddressFields
          idPrefix="billing"
          value={value.billing}
          onChange={(billing) => onChange({ ...value, billing })}
        />
      </div>
      <DuplicateWarning draft={value} excludeId={excludeId} />
      {compact ? null : (
        <>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t("customer.sites")}</h3>
              <Button
                type="button"
                size="small"
                variant="text"
                icon="pi pi-plus"
                onClick={() =>
                  onChange({
                    ...value,
                    sites: [
                      ...value.sites,
                      { ...emptyAddress(), label: "", key: nextSiteKey() },
                    ],
                  })
                }
              >
                {t("customer.addSite")}
              </Button>
            </div>
            {value.sites.length === 0 ? (
              <p className="text-sm text-app-muted">
                {t("customer.sitesHint")}
              </p>
            ) : (
              <ul className="space-y-3">
                {value.sites.map((site, index) => (
                  <li
                    key={site.key}
                    className="rounded-xl border border-app-border p-3"
                  >
                    <div className="mb-3 flex items-end gap-2">
                      <Field label={t("customer.siteLabel")} className="flex-1">
                        <InputText
                          value={site.label}
                          placeholder={t("customer.siteLabelPlaceholder")}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onChange({
                              ...value,
                              sites: value.sites.map((other, i) =>
                                i === index
                                  ? { ...other, label: event.target.value }
                                  : other,
                              ),
                            })
                          }
                        />
                      </Field>
                      <Button
                        type="button"
                        variant="text"
                        severity="danger"
                        icon="pi pi-trash"
                        aria-label={t("common.remove")}
                        onClick={() =>
                          onChange({
                            ...value,
                            sites: value.sites.filter((_, i) => i !== index),
                          })
                        }
                      />
                    </div>
                    <AddressFields
                      idPrefix={site.key}
                      value={site}
                      onChange={(address) =>
                        onChange({
                          ...value,
                          sites: value.sites.map((other, i) =>
                            i === index ? { ...other, ...address } : other,
                          ),
                        })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("customer.documentLanguage")}>
              <SelectInput
                value={value.preferredLocale}
                onChange={(preferredLocale) =>
                  onChange({ ...value, preferredLocale })
                }
                options={[
                  { value: "", label: t("customer.languageDefault") },
                  ...SUPPORTED_LOCALES.map((locale) => ({
                    value: locale,
                    label: t(`locale.${locale}`),
                  })),
                ]}
              />
            </Field>
          </div>
          <Field label={t("common.notes")}>
            <Textarea
              value={value.notes}
              rows={3}
              autoResize
              onChange={set("notes")}
            />
          </Field>
        </>
      )}
    </div>
  );
}
